import { useEffect } from 'react';

import { useUserStore } from '../store';
import { can, isSimplifiedUi, ownCalendarOnly, permissionsPending } from '../utils/access';

// Backoff for a payload without `permissions` (backend not upgraded yet during a deploy).
const REFETCH_DELAYS_MS = [3000, 8000, 20000, 45000, 90000];
let refetchAttempt = 0;
let refetchTimer = null;

/**
 * Permission reads for components: `can(key)` answers from the current organization's
 * effective keys (utils/access.js). A payload served before the backend carried `permissions`
 * (a deploy in progress) is refetched with backoff, so the UI does not stay empty until a reload.
 *
 * Nothing here refetches on window focus or tab visibility: a users/me load replaces
 * `currentOrganization` in the store, which remounts and resets views that depend on it
 * (dialogs jumping back to the first tab, half-typed input lost). An admin's matrix change
 * reaches other sessions on their next natural users/me load or reload.
 */
export default function usePermissions() {
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const fetchUser = useUserStore((s) => s.fetchUser);
  const missing = permissionsPending(currentOrganization);

  useEffect(() => {
    if (!missing) {
      refetchAttempt = 0;
      return;
    }
    if (refetchTimer) return;
    const delay = REFETCH_DELAYS_MS[Math.min(refetchAttempt, REFETCH_DELAYS_MS.length - 1)];
    refetchTimer = setTimeout(() => {
      refetchTimer = null;
      refetchAttempt += 1;
      fetchUser().catch(() => {});
    }, delay);
  }, [missing, fetchUser]);

  return {
    can: (key) => can(currentOrganization, key),
    isSimplifiedUi: isSimplifiedUi(currentOrganization),
    ownCalendarOnly: ownCalendarOnly(currentOrganization),
    ready: !missing,
  };
}
